import { FlashbarProps } from '@amzn/awsui-components-core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteFinanceCalendarEventMutation, fetchFinanceCalendarEvents, putFinanceCalendarEventsMutation } from 'src/api/app-sync-services';
import { FinanceCalendarEventEntity } from 'src/api/interfaces';
import { v4 as uuidv4 } from 'uuid';

/** Per-field error messages for a single event */
export interface EventValidationErrors {
  scenario?: string;
  date?: string;
  event?: string;
}

/** Strip __typename and any other GraphQL metadata from events before sending to mutations */
const cleanEventForMutation = ({ eventId, scenario, dateTime, event, description, source }: FinanceCalendarEventEntity) => ({
  eventId,
  scenario,
  dateTime,
  event,
  description,
  source
});

/** Returns today's date string in YYYY-MM-DD format */
const getTodayDateString = (): string => new Date().toISOString().split('T')[0];

/** Validate manual events: scenario != '-', valid future date, non-empty event name */
const validateManualEvents = (events: FinanceCalendarEventEntity[]): Map<string, EventValidationErrors> => {
  const errors = new Map<string, EventValidationErrors>();
  const today = getTodayDateString();

  for (const e of events) {
    if (e.source !== 'manual') continue;

    const fieldErrors: EventValidationErrors = {};

    // Scenario must not be placeholder
    if (!e.scenario || e.scenario === '-') {
      fieldErrors.scenario = 'Select a scenario';
    }

    // Date must be valid and not in the past
    const [datePart] = (e.dateTime || '').split('T');
    if (!datePart || datePart.length === 0) {
      fieldErrors.date = 'Date is required';
    } else if (datePart < today) {
      fieldErrors.date = 'Date cannot be in the past';
    }

    // Event name must not be empty
    if (!e.event || e.event.trim().length === 0) {
      fieldErrors.event = 'Event name is required';
    }

    if (Object.keys(fieldErrors).length > 0) {
      errors.set(e.eventId, fieldErrors);
    }
  }

  return errors;
};

export const useFinanceCalendarManagement = () => {
  const [events, setEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  const [selectedEvents, setSelectedEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [deletedEventIds, setDeletedEventIds] = useState<string[]>([]);
  const [persistedEventIds, setPersistedEventIds] = useState<Set<string>>(new Set());

  /** Compute validation errors reactively whenever events change */
  const validationErrors = useMemo(() => validateManualEvents(events), [events]);

  useEffect(() => {
    fetchFinanceCalendarEvents()
      .then((data) => {
        setEvents(data);
        setPersistedEventIds(new Set(data.map((e) => e.eventId)));
        setLoading(false);
      })
      .catch((e) => {
        addFlashMessage('error', `Failed to load calendar events: ${e.message}`);
        setLoading(false);
      });
  }, []);

  const addFlashMessage = useCallback((type: FlashbarProps.Type, content: string) => {
    const id = uuidv4();
    setFlashbarItems((prev) => [
      ...prev,
      {
        type,
        content,
        dismissible: true,
        dismissLabel: 'Dismiss',
        id,
        onDismiss: () => setFlashbarItems((items) => items.filter((item) => item.id !== id))
      }
    ]);
  }, []);

  /** Add a new blank event at the top of the table */
  const handleAddBlankEvent = useCallback(() => {
    const blankEvent: FinanceCalendarEventEntity = {
      eventId: uuidv4(),
      scenario: '-',
      dateTime: 'T00:00:00',
      event: '',
      description: '',
      source: 'manual'
    };
    setEvents((prev) => [blankEvent, ...prev]);
    setSelectedEvents([blankEvent]);
    setIsDirty(true);
  }, []);

  /** Update a single field on an event by eventId */
  const handleUpdateEventField = useCallback((eventId: string, field: keyof FinanceCalendarEventEntity, value: string) => {
    setEvents((prev) => prev.map((e) => (e.eventId === eventId ? { ...e, [field]: value } : e)));
    setIsDirty(true);
  }, []);

  const handleDeleteEvents = useCallback(() => {
    const selectedIds = selectedEvents.map((e) => e.eventId);
    // Only queue deletion API calls for events that exist on the backend
    setDeletedEventIds((prev) => [...prev, ...selectedIds.filter((id) => persistedEventIds.has(id))]);
    setEvents((prev) => prev.filter((e) => !selectedIds.includes(e.eventId)));
    setSelectedEvents([]);
    setIsDirty(true);
  }, [selectedEvents, persistedEventIds]);

  const handleSave = useCallback(async () => {
    // Trigger validation display
    setShowValidation(true);

    // Check for validation errors on manual events
    const errors = validateManualEvents(events);
    if (errors.size > 0) {
      addFlashMessage('error', `Please fix ${errors.size} event(s) with validation errors before saving.`);
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        putFinanceCalendarEventsMutation(events.map(cleanEventForMutation)),
        ...deletedEventIds.map((id) => deleteFinanceCalendarEventMutation(id))
      ]);
      setDeletedEventIds([]);
      // After save, all current events are now persisted on the backend
      setPersistedEventIds(new Set(events.map((e) => e.eventId)));
      setIsDirty(false);
      setShowValidation(false);
      addFlashMessage('success', 'Finance calendar events saved successfully.');
    } catch (e: any) {
      addFlashMessage('error', `Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [events, deletedEventIds, addFlashMessage]);

  return {
    // Data
    events,
    loading,
    saving,
    isDirty,
    flashbarItems,

    // Validation
    validationErrors,
    showValidation,

    // Selection
    selectedEvents,
    setSelectedEvents,

    // Actions
    handleAddBlankEvent,
    handleUpdateEventField,
    handleDeleteEvents,
    handleSave
  };
};
